import classNames from 'classnames'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import MaterialIcon from '../../components/MaterialIcon'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import { Bilancia, PaginatedDJResponse } from '../../types'
import {
  calcGiorni,
  getLabelTipoBilancia,
  useIsAmministratore,
  useIsCliente,
  useIsSupervisore,
} from '../../utils/utils'

export default function BilanceTable({
  bilance,
  uiFilters,
  setFilters,
  showSede = false,
  hasFiltersActive = false,
  modalActionsForm,
  modalActionsDelete,
  isLoading,
}: {
  bilance: PaginatedDJResponse<Bilancia> | undefined
  uiFilters: {
    ordering: string
  }
  setFilters: (filters: Record<string, string>) => void
  showSede?: boolean
  hasFiltersActive?: boolean
  modalActionsForm?: {
    open: (v?: Bilancia | null | undefined) => void
    close: () => void
    toggle: () => void
    onClosed: () => void
  }
  modalActionsDelete?: {
    open: (v?: Bilancia | null | undefined) => void
    close: () => void
    toggle: () => void
    onClosed: () => void
  }
  isLoading?: boolean
}) {
  const [openMoreBilancia, setOpenMoreBilancia] = useState<number | null>(null)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.target instanceof Element && !e.target.closest(`#bilancia-${openMoreBilancia}`)) {
        setOpenMoreBilancia(null)
      }
    }
    document.addEventListener('click', handleClick)
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [openMoreBilancia])

  const isAdmin = useIsAmministratore()
  const isSupervisore = useIsSupervisore()
  const isCliente = useIsCliente()

  return (
    <StickyTable
      isLoading={isLoading}
      className={classNames({
        StickyTableSmaller: showSede && !hasFiltersActive,
        StickyWithFilters: hasFiltersActive && !showSede,
        StickyWithFiltersAndSede: hasFiltersActive && showSede,
      })}
    >
      <thead className="position-sticky top-0 bg-white">
        <Sorter
          value={uiFilters.ordering}
          onSortChange={(ordering) =>
            setFilters({
              ordering,
            })
          }
        >
          <tr className="border-bottom-0">
            <th colSpan={isCliente ? 4 : 3} className="bg-white"></th>
            <th colSpan={5} className="bg-intervento-periodico">
              <span className="text-intervento-periodico p-md-bold ps-2">Intervento Verificazione Periodica</span>
            </th>
            <th colSpan={4} className="bg-rapporto-taratura">
              <span className="text-rapporto-taratura p-md-bold ps-2">Intervento Rapporto Taratura</span>
            </th>
          </tr>
          <tr>
            <th style={{ width: '40%' }}>
              <div className="d-flex align-items-center">
                Matricola
                <SortControl field="matricola" />
              </div>
            </th>
            <th></th>
            {!showSede && !isCliente && (
              <th style={{ width: '60%' }}>
                <div className="d-flex align-items-center">
                  Cliente
                  <SortControl field="sede__cliente__ragione_sociale" />
                </div>
              </th>
            )}
            {showSede && !isCliente && (
              <th style={{ width: '30%' }}>
                <div className="d-flex align-items-center">
                  Sede
                  <SortControl field="sede_sort" />
                </div>
              </th>
            )}
            {isCliente && (
              <>
                <th style={{ width: '60%' }}>
                  <div className="d-flex align-items-center">
                    Cliente
                    <SortControl field="sede__cliente__ragione_sociale" />
                  </div>
                </th>
                <th style={{ width: '30%' }}>
                  <div className="d-flex align-items-center">
                    Sede
                    <SortControl field="sede__via" />
                  </div>
                </th>
              </>
            )}
            <th className="bg-intervento-periodico fw-400">Ultimo</th>
            <th className="bg-intervento-periodico fw-400">Scadenza</th>
            <th className="bg-intervento-periodico fw-400">Richiesta</th>
            <th className="bg-intervento-periodico fw-400">GG</th>
            <th className="bg-intervento-periodico fw-400">Program.</th>
            <th className="bg-rapporto-taratura fw-400">Cadenza</th>
            <th className="bg-rapporto-taratura fw-400">Ultimo</th>
            <th className="bg-rapporto-taratura fw-400">Scadenza</th>
            <th className="bg-rapporto-taratura fw-400">Program.</th>
          </tr>
        </Sorter>
      </thead>
      <tbody>
        {bilance?.count === 0 && (
          <tr>
            <td colSpan={12} className="text-center">
              Nessuna bilancia disponibile
            </td>
          </tr>
        )}
        {bilance?.results?.map((bilancia) => (
          <tr key={bilancia.id}>
            <td>
              <div className="d-flex align-items-center">
                <Link
                  className="link-md"
                  to={`/bilance/${bilancia.id}`}
                  style={{
                    color: bilancia.fuori_uso ? 'red' : bilancia.dismessa ? '#6610f2' : '',
                  }}
                >
                  {bilancia.matricola} {bilancia.sigla ? `(${bilancia.sigla})` : ''}
                </Link>
                {bilancia.data_richiesta_periodica && calcGiorni(bilancia.data_richiesta_periodica) > 0 && (
                  <MaterialIcon
                    icon="timer"
                    className="text-intervento-periodico ms-2"
                    title="Bilancia con richiesta di intervento periodico"
                  />
                )}
                {!bilancia.intervento_aperto_periodica?.stato && bilancia.verif_periodica && (
                  <span
                    className={classNames('material-icons text-intervento-periodico', {
                      'ms-1': bilancia.data_richiesta_periodica && calcGiorni(bilancia.data_richiesta_periodica) > 0,
                      'ms-2': !bilancia.data_richiesta_periodica || calcGiorni(bilancia.data_richiesta_periodica) <= 0,
                    })}
                    style={{ fontSize: 16 }}
                    title="Bilancia senza richiesta di intervento programmato"
                  >
                    event_busy
                  </span>
                )}
                {!bilancia.intervento_aperto_taratura?.stato && bilancia.rapporto_taratura && (
                  <span
                    className={classNames('ms-1 material-icons text-rapporto-taratura', {
                      'ms-1': !bilancia.intervento_aperto_periodica?.stato,
                      'ms-2': bilancia.intervento_aperto_periodica?.stato && !bilancia.data_richiesta_periodica,
                    })}
                    style={{ fontSize: 16 }}
                    title="Bilancia senza intervento rapporto taratura programmato"
                  >
                    event_busy
                  </span>
                )}

                {bilancia.fuori_uso && (
                  <MaterialIcon icon="error" className="text-danger ms-2" title="Bilancia fuori uso" />
                )}
                {bilancia.dismessa && (
                  <MaterialIcon icon="error" className="text-rapporto-taratura ms-2" title="Bilancia dismessa" />
                )}
              </div>
              <div className="p-md text-body-secondary">{getLabelTipoBilancia(bilancia.tipo_bilancia)}</div>
            </td>
            <td>
              {!isCliente && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (openMoreBilancia === bilancia.id) {
                      setOpenMoreBilancia(null)
                    } else {
                      setOpenMoreBilancia(bilancia.id)
                    }
                  }}
                  variant="light"
                  className="text-secondary d-flex align-items-center"
                  type="button"
                >
                  <span className="material-icons">more_horiz</span>
                </Button>
              )}
              {openMoreBilancia === bilancia.id && (
                <div
                  id={`bilancia-${bilancia.id}`}
                  className="list-group position-absolute"
                  // ref={editDeleteRef}
                  style={{
                    // zIndex: 1,
                    width: '200px',
                    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
                    borderRadius: '5px',
                  }}
                >
                  <button
                    onClick={() => {
                      modalActionsForm?.open(bilancia)
                    }}
                    className="list-group-item list-group-item-action d-flex align-items-center"
                  >
                    <span
                      className="material-icons me-2"
                      style={{
                        fontSize: '16px',
                      }}
                    >
                      edit
                    </span>
                    Modifica
                  </button>
                  {(isAdmin || isSupervisore) && (
                    <button
                      onClick={() => {
                        modalActionsDelete?.open(bilancia)
                      }}
                      className="list-group-item list-group-item-action d-flex align-items-center text-danger"
                    >
                      <span
                        className="material-icons me-2"
                        style={{
                          fontSize: '16px',
                        }}
                      >
                        delete
                      </span>
                      Elimina
                    </button>
                  )}
                </div>
              )}
            </td>
            {!showSede && !isCliente && (
              <td>
                <div className="d-flex align-items-center">
                  <Link className={'link-on-hover'} to={`/clienti/${bilancia.sede_data.cliente}`}>
                    {bilancia.cliente_ragione_sociale}
                  </Link>
                </div>
                <div className="p-md text-body-secondary">
                  {bilancia.sede_data.numero_rea} - {bilancia.reparto}
                </div>
              </td>
            )}
            {showSede && !isCliente && (
              <td>
                {bilancia.sede_data.toponimo_data?.nome} {bilancia.sede_data.via}, {bilancia.sede_data.civico} -{' '}
                {bilancia.sede_data.comune_data?.nome} ({bilancia.sede_data.provincia_data?.sigla}) -{' '}
                <span className="text-body-secondary">{bilancia.reparto}</span>
              </td>
            )}
            {isCliente && (
              <>
                <td>
                  <div className="d-flex align-items-center">
                    <Link className={'link-on-hover'} to={`/clienti/${bilancia.sede_data.cliente}`}>
                      {bilancia.cliente_ragione_sociale}
                    </Link>
                  </div>
                  <div className="p-md text-body-secondary">
                    {bilancia.sede_data.numero_rea} - {bilancia.reparto}
                  </div>
                </td>
                <td>
                  {bilancia.sede_data.toponimo_data?.nome} {bilancia.sede_data.via}, {bilancia.sede_data.civico} -{' '}
                  {bilancia.sede_data.comune_data?.nome} ({bilancia.sede_data.provincia_data?.sigla}) -{' '}
                  <span className="text-body-secondary">{bilancia.reparto}</span>
                </td>
              </>
            )}
            <td className="bg-intervento-periodico">
              {bilancia.data_intervento_periodica && (
                <div>{dayjs(bilancia.data_intervento_periodica).format('DD/MM/YYYY')}</div>
              )}
              {bilancia.ultimo_intevento_periodica?.esito && (
                <div
                  className={classNames('text-capitalize p-sm d-flex align-items-center', {
                    'text-success': bilancia.ultimo_intevento_periodica.esito === 'positivo',
                    'text-danger': bilancia.ultimo_intevento_periodica.esito === 'negativo',
                  })}
                >
                  {bilancia.ultimo_intevento_periodica.esito === 'positivo' ? (
                    <span
                      className="material-icons me-1"
                      style={{
                        fontSize: 16,
                      }}
                    >
                      check
                    </span>
                  ) : (
                    <span
                      className="material-icons me-1"
                      style={{
                        fontSize: 16,
                      }}
                    >
                      close
                    </span>
                  )}
                  {bilancia.ultimo_intevento_periodica.esito}
                </div>
              )}
            </td>
            <td className="bg-intervento-periodico">
              {bilancia.data_scadenza_periodica && (
                <div>{dayjs(bilancia.data_scadenza_periodica).format('DD/MM/YYYY')}</div>
              )}
              {bilancia.data_scadenza_periodica &&
                dayjs(bilancia.data_scadenza_periodica).diff(dayjs(), 'days') < 0 && (
                  <div
                    className={classNames('text-danger p-sm d-flex align-items-center', {
                      'text-success': dayjs(bilancia.data_scadenza_periodica).diff(dayjs(), 'days') > 0,
                    })}
                  >
                    <span
                      className="material-icons me-1"
                      style={{
                        fontSize: 16,
                      }}
                    >
                      priority_high
                    </span>
                    Scaduto
                  </div>
                )}
            </td>
            <td className="bg-intervento-periodico">
              {bilancia.data_richiesta_periodica && (
                <div>{dayjs(bilancia.data_richiesta_periodica).format('DD/MM/YYYY')}</div>
              )}
            </td>
            <td className="bg-intervento-periodico">
              {bilancia.data_richiesta_periodica && calcGiorni(bilancia.data_richiesta_periodica) > 0 && (
                <div>{calcGiorni(bilancia.data_richiesta_periodica)}</div>
              )}
            </td>
            <td className="bg-intervento-periodico">
              <div className="d-flex align-items-center">
                {bilancia?.intervento_aperto_periodica?.stato &&
                  bilancia?.intervento_aperto_periodica?.stato !== 'richiesto' && (
                    <Link
                      className={classNames('link-md d-flex align-items-center', {
                        'text-intervento-periodico':
                          bilancia?.intervento_aperto_periodica?.stato === 'eseguito' ||
                          bilancia?.intervento_aperto_periodica?.stato === 'riesaminato' ||
                          bilancia?.intervento_aperto_periodica?.stato === 'esportato',
                      })}
                      to={`/interventi/${bilancia?.intervento_aperto_periodica?.pk}`}
                    >
                      {dayjs(bilancia?.intervento_aperto_periodica?.data_intervento).format('DD/MM/YYYY')}
                    </Link>
                  )}
                {bilancia?.intervento_aperto_periodica?.stato &&
                  (bilancia?.intervento_aperto_periodica?.stato === 'eseguito' ||
                    bilancia?.intervento_aperto_periodica?.stato === 'riesaminato' ||
                    bilancia?.intervento_aperto_periodica?.stato === 'esportato') && (
                    <span className="material-icons text-intervento-periodico ms-1" style={{ fontSize: 16 }}>
                      check
                    </span>
                  )}
              </div>
            </td>
            <td className="bg-rapporto-taratura">{bilancia.periodo_controllo_qualita}</td>
            <td className="bg-rapporto-taratura">
              {bilancia.data_intervento_taratura && bilancia.ultimo_intevento_taratura?.stato === 'eseguito' && (
                <div>{dayjs(bilancia.data_intervento_taratura).format('DD/MM/YYYY')}</div>
              )}
            </td>
            <td className="bg-rapporto-taratura">
              {bilancia.data_scadenza_taratura && (
                <div>{dayjs(bilancia.data_scadenza_taratura).format('DD/MM/YYYY')}</div>
              )}
            </td>
            <td className="bg-rapporto-taratura">
              {bilancia.intervento_aperto_taratura?.stato && (
                <Link to={`/interventi/${bilancia.intervento_aperto_taratura.pk}`} className="link-md">
                  {dayjs(bilancia.intervento_aperto_taratura?.data_intervento).format('DD/MM/YYYY')}
                </Link>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </StickyTable>
  )
}
